import * as React from 'react';
import { cn } from '@/lib/utils';
import { cva } from 'class-variance-authority';
import { EmptyState } from '../custom/empty-state';

const Table = React.forwardRef(({ className, ...props }, ref) => (
  <div className="relative w-full overflow-auto">
    <table
      ref={ref}
      className={cn('w-full max-w-full table-fixed caption-bottom border-separate border-spacing-0 border-spacing-y-[10px] text-xs text-neutral', className)}
      {...props}
    />
  </div>
));
Table.displayName = 'Table';

const TableHeader = React.forwardRef(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn('[&_tr]:border-b', className)}
    {...props}
  />
));
TableHeader.displayName = 'TableHeader';

const TableHeaderWrapper = React.forwardRef(({ className, tableHead = [], ...props }, ref) => (
  <TableHeader
    ref={ref}
    className={cn('c-table-header-wrapper', className)}
    {...props}
  >
    <TableRow>
      {tableHead.map((head, index) => {
        const dynamicStyles = {
          minWidth: head.minWidth ? `${head.minWidth}px` : undefined,
          width: head.width ? `${head.width}px` : undefined
        };

        return (
          <TableHead
            key={index}
            scope="col"
            style={dynamicStyles}
            className={cn(!head.width && 'w-auto', 'whitespace-nowrap')}
          >
            {head.label}
          </TableHead>
        );
      })}
    </TableRow>
  </TableHeader>
));
TableHeaderWrapper.displayName = 'TableHeaderWrapper';

const TableBody = React.forwardRef(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn('[&_tr:last-child]:border-0', className)}
    {...props}
  />
));
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn('border-t bg-muted/50 font-medium [&>tr]:last:border-b-0', className)}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

const tableRowVariants = cva('border-b transition-colors data-[state=selected]:bg-muted', {
  variants: {
    highlight: {
      true: '[&>td]:text-white [&>th]:text-white [&_.c-table-user-title]:text-white [&_svg]:text-white'
    },
    danger: {
      true: '[&>td]:bg-danger-lightest [&>th]:!bg-danger-lightest [&>td]:border-danger-lighter [&>th]:!border-danger-lighter'
    }
  },
  defaultVariants: {
    highlight: false
  }
});

const DEFAULT_HIGHLIGHT_BG_CLASSNAME = '[&>td]:bg-primary-light [&>th]:!bg-primary-light';

const TableRow = React.forwardRef(({ className, highlight, highlightClassName = DEFAULT_HIGHLIGHT_BG_CLASSNAME, danger, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      tableRowVariants({ highlight, danger }),
      highlight && highlightClassName,
      '[&>:last-child]:rounded-br-lg [&>:last-child]:rounded-tr-lg [&>:last-child]:border-r [&>:last-child]:pr-3',
      '[&>:first-child]:rounded-bl-lg [&>:first-child]:rounded-tl-lg [&>:first-child]:border-l [&>:first-child]:pl-3',
      className
    )}
    {...props}
  />
));
TableRow.displayName = 'TableRow';

const TableHead = React.forwardRef(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      'truncate whitespace-nowrap px-2 text-left align-middle font-bold [&:has([role=checkbox])]:pr-0',
      '[tbody_&]:border-y [tbody_&]:border-solid [tbody_&]:border-neutral-lightest [tbody_&]:bg-white [tbody_&]:shadow-[2px_2px_2px_1px_rgba(68,43,140,0.03)]',
      className
    )}
    {...props}
  />
));
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      'c-table-cell',
      // styles
      'text-neutral-dark',
      'border-y border-solid border-neutral-lightest bg-white bg-clip-padding px-2 py-3 align-middle shadow-[2px_2px_2px_1px_rgba(68,43,140,0.03)] [&:has([role=checkbox])]:pr-0',
      className
    )}
    {...props}
  />
));
TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn('mt-4 text-sm text-muted-foreground', className)}
    {...props}
  />
));
TableCaption.displayName = 'TableCaption';

const TableUser = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-table-user', 'flex min-w-[100px] max-w-full items-center gap-3', className)}
    {...props}
  />
));
TableUser.displayName = 'TableUser';

const TableUserContent = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-table-user-content', 'flex w-0 grow flex-wrap items-center gap-[0.2em]', className)}
    {...props}
  />
));
TableUserContent.displayName = 'TableUserContent';

const TableUserTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn('c-table-user-title', 'mb-0 w-full text-xs font-bold leading-normal text-primary-dark', className)}
    {...props}
  />
));
TableUserTitle.displayName = 'TableUserTitle';

const TableNoData = React.forwardRef(({ className, notFound, colSpan = 12, emptyState, ...props }, ref) => {
  return (
    notFound && (
      <TableRow
        ref={ref}
        className={cn('c-table-no-data', className)}
        {...props}
      >
        <TableCell
          colSpan={colSpan}
          className="bg-neutral-bright shadow-none"
        >
          {emptyState ? (
            emptyState
          ) : (
            <EmptyState
              type="data"
              variant="plain"
            />
          )}
        </TableCell>
      </TableRow>
    )
  );
});
TableNoData.displayName = 'TableNoData';

const TableTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn('c-table-title', 'mb-0 line-clamp-3 min-w-5 flex-grow text-base font-bold leading-tight text-primary-dark', className)}
    {...props}
  />
));
TableTitle.displayName = 'TableTitle';

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableHeaderWrapper, TableRow, TableCell, TableCaption, TableUser, TableUserContent, TableUserTitle, TableNoData, TableTitle };
